import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ApiEndpoints from './services/ApiEndpoints';
import './App.css';

function App() {
  const [selectedGiftBox, setSelectedGiftBox] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerObservations, setCustomerObservations] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(12);
  const [products, setProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const whatsappNumber = "573246855854";

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const mapProductFromApi = (p) => {
    const cat = p?.categories?.name || p?.category || '';
    const imgList = p?.images || [];
    return {
      id: Number(p?.id) || 0,
      name: p?.name,
      category: cat,
      categoryColor: p?.categories?.color,
      categoryIcon: p?.categories?.icon,
      price: p?.price ?? 0,
      discountedPrice: p?.discounted_price ?? p?.discountedPrice,
      discountPercentage: p?.discount_percentage ?? p?.discountPercentage,
      stock: p?.stock ?? 0,
      inStock: p?.in_stock ?? p?.inStock ?? true,
      published: p?.published ?? true,
      description: p?.description ?? '',
      details: p?.details ?? '',
      tags: Array.isArray(p?.tags) ? p.tags : (p?.tags ? [p.tags] : []),
      images: imgList
    };
  };

  const loadProducts = async () => {
    try {
      const data = await ApiEndpoints.getProducts();
      const mapped = (data || []).map(mapProductFromApi);
      setProducts(mapped);
    } catch (error) {
      console.error('Error loading products from ApiEndpoints:', error);
      setProducts([]);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await ApiEndpoints.getCategories();
      const mapped = (data || []).map(c => ({
        ...c,
        id: Number(c.id) || c.id
      }));
      setCategories(mapped.filter(c => c.active));
    } catch (error) {
      console.error('Error loading categories from ApiEndpoints:', error);
      setCategories([]);
    }
  };

  const categoryOptions = ['Todos', ...categories.map(c => c.name)];
  
  // Filter by search term
  const searchFiltered = searchTerm 
    ? products.filter(p => {
        const term = searchTerm.toLowerCase();
        return p.name?.toLowerCase().includes(term) || 
               p.description?.toLowerCase().includes(term) ||
               p.tags?.some(t => t.toLowerCase().includes(term)) ||
               p.category?.toLowerCase().includes(term);
      })
    : products;

  const filteredProducts = selectedCategory === 'Todos' 
    ? [...searchFiltered.filter(p => p.published)].sort((a, b) => {
        if (a.inStock && !b.inStock) return -1;
        if (!a.inStock && b.inStock) return 1;
        return 0;
      }) 
    : [...searchFiltered.filter(p => p.category === selectedCategory && p.published)].sort((a, b) => {
        if (a.inStock && !b.inStock) return -1;
        if (!a.inStock && b.inStock) return 1;
        return 0;
      });

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    const productsSection = document.getElementById('anchetas');
    console.log(productsSection)
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const addToCart = (product, e) => {
    e.stopPropagation();
    
    // Verificar stock disponible
    if (!product.inStock || product.stock <= 0) {
      alert('Este producto está agotado. Por favor selecciona otro producto.');
      return;
    }
    
    // Verificar si ya está en el carrito y calcular cantidad total
    const existingItem = cart.find(item => item.id === product.id);
    const currentQuantity = existingItem ? existingItem.quantity : 0;
    
    if (currentQuantity >= product.stock) {
      alert(`Solo hay ${product.stock} unidades disponibles de este producto.`);
      return;
    }
    
    if (existingItem) {
      setCart(cart.map(item => item.id === product.id ? {...item, quantity: item.quantity + 1} : item));
    } else {
      setCart([...cart, {...product, quantity: 1}]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    const product = products.find(p => p.id === productId);
    
    if (newQuantity < 1) {
      removeFromCart(productId);
    } else if (product && newQuantity > product.stock) {
      alert(`Solo hay ${product.stock} unidades disponibles de este producto.`);
    } else {
      setCart(cart.map(item => item.id === productId ? {...item, quantity: newQuantity} : item));
    }
  };

  const getTotal = () => {
    return cart.reduce((total, item) => {
      const itemPrice = item.discountedPrice || item.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const formatPrice = (price) => {
    return "$" + price.toLocaleString('es-CO');
  };

  const saveOrderToApi = async (orderData) => {
    try {
      // Construir payload para API
      const payload = {
        customerName: orderData.customer.name,
        customerPhone: orderData.customer.phone,
        customerAddress: orderData.customer.address,
        total: orderData.total,
        observations: orderData.observations || '',
        termsAccepted: orderData.termsAccepted,
        privacyAccepted: orderData.privacyAccepted,
        items: orderData.items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          discountPercentage: item.discountPercentage,
          discountedPrice: item.discountedPrice,
          quantity: item.quantity,
          subtotal: item.subtotal
        }))
      };
      const result = await ApiEndpoints.createOrder(payload);
      if (result) {
        console.log('Pedido creado en API Endpoint:', result);
        alert('✅ Pedido guardado exitosamente');
      } else {
        console.log('Pedido no guardado en API Endpoint');
        alert('❌ Error al guardar el pedido');
      }
      return result;
    } catch (error) {
      console.error('Error saving order via API:', error);
      alert('❌ Error al guardar el pedido. Por favor intenta de nuevo.');
      return null;
    }
  };

  const sendToWhatsApp = () => {
    if (!customerName || !customerPhone || !customerAddress) {
      alert("Por favor completa todos los datos de contacto");
      return;
    }

    if (!acceptTerms || !acceptPrivacy) {
      alert("Debes aceptar los Términos y Condiciones y la Política de Tratamiento de Datos para continuar");
      return;
    }

    // Deshabilitar botón para evitar doble envío
    setIsSubmitting(true);

    // Preparar datos del pedido
    const orderData = {
      customer: {
        name: customerName,
        phone: customerPhone,
        address: customerAddress
      },
      items: cart.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        discountedPrice: item.discountedPrice,
        discountPercentage: item.discountPercentage,
        quantity: item.quantity,
        subtotal: (item.discountedPrice || item.price) * item.quantity
      })),
      total: getTotal(),
      observations: customerObservations.trim(),
      termsAccepted: acceptTerms,
      privacyAccepted: acceptPrivacy
    };

    // Guardar pedido via API
    saveOrderToApi(orderData);

    let message = `🎁 *NUEVO PEDIDO GIFTBOX*\n\n`;
    message += `👤 *Cliente:* ${customerName}\n`;
    message += `📞 *Teléfono:* ${customerPhone}\n`;
    message += `📍 *Dirección:* ${customerAddress}\n\n`;
    message += `🛒 *PRODUCTOS:*\n`;
    message += `─────────────────\n`;

    cart.forEach(item => {
      const itemPrice = item.discountedPrice || item.price;
      const totalPrice = itemPrice * item.quantity;
      
      message += `• ${item.name}\n`;
      message += `  Cantidad: ${item.quantity}\n`;
      if (item.discountedPrice) {
        message += `  Precio: ${formatPrice(item.price)} → ${formatPrice(item.discountedPrice)} (-${item.discountPercentage}%)\n`;
        message += `  Total: ${formatPrice(totalPrice)}\n\n`;
      } else {
        message += `  Precio: ${formatPrice(itemPrice * item.quantity)}\n\n`;
      }
    });

    message += `─────────────────\n`;
    message += `💰 *TOTAL: ${formatPrice(getTotal())}*\n\n`;
    
    if (customerObservations.trim()) {
      message += `📝 *OBSERVACIONES:*\n`;
      message += `${customerObservations}\n\n`;
    }
    
    message += `📋 *ACEPTACIÓN:*\n`;
    message += `✅ Términos y Condiciones: Aceptados\n`;
    message += `✅ Política de Tratamiento de Datos: Aceptada\n\n`;
    
    message += `¡Gracias por tu compra! 💕`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    // Habilitar botón después de un breve retraso
    setTimeout(() => {
      setIsSubmitting(false);
    }, 3000);
  };

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  const handlePreviousImage = () => {
    if (selectedGiftBox && selectedGiftBox.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedGiftBox.images.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (selectedGiftBox && selectedGiftBox.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === selectedGiftBox.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleImageClick = (product) => {
    setSelectedGiftBox(product);
    setCurrentImageIndex(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Modal Detalles del Producto con Carrusel */}
      {selectedGiftBox && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedGiftBox(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">{selectedGiftBox.name}</h3>
              <button 
                onClick={() => setSelectedGiftBox(null)} 
                className="text-white hover:bg-white/20 w-8 h-8 rounded-full flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            {/* Contenido */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Columna Izquierda - Carrusel de Imágenes */}
                <div className="relative bg-gray-100">
                  {/* Imagen principal */}
                  <img 
                    src={selectedGiftBox.images[currentImageIndex]} 
                    alt={`${selectedGiftBox.name} - Imagen ${currentImageIndex + 1}`} 
                    className="w-full h-[600px] object-cover"
                  />
                  
                  {/* Controles del carrusel */}
                  {selectedGiftBox.images.length > 1 && (
                    <>
                      <button 
                        onClick={handlePreviousImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 text-gray-800 w-10 h-10 rounded-full flex items-center justify-center hover:bg-pink-500 hover:text-white transition font-bold text-xl shadow-lg"
                      >
                        ‹
                      </button>

                      <button 
                        onClick={handleNextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 text-gray-800 w-10 h-10 rounded-full flex items-center justify-center hover:bg-pink-500 hover:text-white transition font-bold text-xl shadow-lg"
                      >
                        ›
                      </button>

                      {/* Indicadores */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 px-3 py-2 rounded-full">
                        {selectedGiftBox.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75'
                            }`}
                          />
                        ))}
                      </div>

                      {/* Contador */}
                      <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} / {selectedGiftBox.images.length}
                      </div>
                    </>
                  )}

                  {/* Miniaturas */}
                  {selectedGiftBox.images.length > 1 && (
                    <div className="flex gap-2 p-2 overflow-x-auto bg-gray-100">
                      {selectedGiftBox.images.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                            index === currentImageIndex ? 'border-pink-500' : 'border-transparent hover:border-pink-300'
                          }`}
                        >
                          <img src={img} alt={`Miniatura ${index + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Columna Derecha - Información del Producto */}
                <div className="p-6">
                  {/* Categoría */}
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-pink-100 text-pink-600 rounded-full text-sm font-medium">
                      {selectedGiftBox.category?.name || selectedGiftBox.category}
                    </span>
                  </div>

                  {/* Etiquetas */}
                  {selectedGiftBox.tags && selectedGiftBox.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {selectedGiftBox.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Precio */}
                  <div className="mb-4">
                    {selectedGiftBox.discountedPrice ? (
                      <div className="flex items-center gap-3">
                        <span className="text-3xl font-bold text-pink-500">{formatPrice(selectedGiftBox.discountedPrice)}</span>
                        <span className="text-lg text-gray-400 line-through">{formatPrice(selectedGiftBox.price)}</span>
                        <span className="bg-red-500 text-white px-2 py-1 rounded-full text-sm font-bold">
                          -{selectedGiftBox.discountPercentage}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-3xl font-bold text-pink-500">{formatPrice(selectedGiftBox.price)}</span>
                    )}
                  </div>

                  {/* Descripción */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Descripción</h4>
                    <p className="text-gray-600 text-sm">{selectedGiftBox.description}</p>
                  </div>

                  {/* Detalles HTML */}
                  {selectedGiftBox.details && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Detalles</h4>
                      <div 
                        className="text-gray-600 text-sm prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: selectedGiftBox.details }}
                      />
                    </div>
                  )}

                  {/* Stock */}
                  <div className="mb-4">
                    {selectedGiftBox.in_stock !== false ? (
                      <span className="text-green-600 font-medium">✓ Stock disponible: {selectedGiftBox.stock} unidades</span>
                    ) : (
                      <span className="text-red-500 font-medium">✕ Sin stock disponible</span>
                    )}
                  </div>

                  {/* Botón agregar al carrito */}
                  <button 
                    onClick={() => addToCart(selectedGiftBox)}
                    disabled={selectedGiftBox.in_stock === false}
                    className={`w-full py-3 rounded-full font-semibold transition ${
                      selectedGiftBox.in_stock === false 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-lg hover:scale-105'
                    }`}
                  >
                    🛒 Agregar al Carrito
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Carrito */}
      {showCart && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowCart(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">🛒 Tu Carrito</h3>
              <button onClick={() => setShowCart(false)} className="text-white hover:bg-white/20 w-8 h-8 rounded-full flex items-center justify-center">✕</button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {cart.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Tu carrito está vacío</p>
              ) : (
                <>
                  {cart.map(item => (
                    <div key={item.id} className="flex gap-3 mb-4 pb-4 border-b">
                      <img src={item.images[0]} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 text-sm">{item.name}</h4>
                        <div className="flex flex-col">
                          {item.discountedPrice ? (
                            <>
                              <span className="text-sm text-gray-400 line-through">{formatPrice(item.price)}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-green-600 font-bold">{formatPrice(item.discountedPrice)}</span>
                                <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full font-bold">
                                  -{item.discountPercentage}%
                                </span>
                              </div>
                            </>
                          ) : (
                            <span className="text-pink-500 font-bold">{formatPrice(item.price)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="bg-gray-200 w-6 h-6 rounded-full text-sm hover:bg-pink-200">-</button>
                          <span className="font-semibold">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="bg-gray-200 w-6 h-6 rounded-full text-sm hover:bg-pink-200">+</button>
                          <button onClick={() => removeFromCart(item.id)} className="ml-auto text-red-500 text-sm hover:underline">Eliminar</button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="bg-gray-50 p-4 rounded-xl mt-4">
                    <h4 className="font-semibold text-gray-800 mb-3">📋 Datos de envío</h4>
                    <input
                      type="text"
                      placeholder="Tu nombre"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full p-2 border rounded-lg mb-2 text-sm"
                    />
                    <input
                      type="tel"
                      placeholder="Tu teléfono"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full p-2 border rounded-lg mb-2 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Dirección de entrega"
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      className="w-full p-2 border rounded-lg mb-2 text-sm"
                    />
                    <textarea
                      placeholder="Observaciones del pedido (opcional)"
                      value={customerObservations}
                      onChange={(e) => setCustomerObservations(e.target.value)}
                      className="w-full p-2 border rounded-lg text-sm mb-3"
                      rows="3"
                    />
                    
                    {/* Checkboxes de términos y privacidad */}
                    <div className="space-y-2 text-xs">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span className="text-gray-600">
                          Acepto los <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Términos y Condiciones</a> de GiftBox
                        </span>
                      </label>
                      
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acceptPrivacy}
                          onChange={(e) => setAcceptPrivacy(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span className="text-gray-600">
                          Autorizo el <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">tratamiento de mis datos personales</a> según la política de privacidad
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-4 border-t bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Total:</span>
                  <span className="text-2xl font-bold text-pink-500">{formatPrice(getTotal())}</span>
                </div>
                <button 
                  onClick={sendToWhatsApp}
                  disabled={isSubmitting}
                  className={`w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 transition ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  <span className="text-xl">{isSubmitting ? '⏳' : '📱'}</span> 
                  {isSubmitting ? 'Enviando...' : 'Enviar Pedido por WhatsApp'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="https://cdn.abacus.ai/images/ce62aaea-7192-4eab-8134-3539efec6757.png" alt="GiftBox Logo" className="w-12 h-12 rounded-lg object-cover" />
            <span className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">GiftBox</span>
          </div>
          <nav className="hidden md:flex gap-6 text-gray-600">
            <a href="#anchetas" className="hover:text-pink-500 transition">Colecciones</a>
            <a href="#personalizado" className="hover:text-pink-500 transition">Personalizado</a>
            <a href="#empresas" className="hover:text-pink-500 transition">Empresas</a>
          </nav>
            <button onClick={() => setShowCart(true)} className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg transition flex items-center gap-2 relative">
            🛒 Carrito
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold">{cartItemCount}</span>
            )}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-pink-100 text-pink-600 px-4 py-1 rounded-full text-sm font-medium mb-4">✨ Envío gratis según región, aplican condiciones</span>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800 leading-tight mb-6">
                Regala <span className="text-pink-500">Momentos</span> Inolvidables
              </h1>
              <p className="text-lg text-gray-600 mb-6">Sorprende a quienes amas con los exclusivos regalos GiftBox, llenos de productos premium.</p>
              
              {/* Buscador */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="🔍 Buscar productos..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (e.target.value.length > 0) {
                        setSelectedCategory('Todos');
                      }
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="w-full px-6 py-4 pr-12 rounded-full border-2 border-gray-200 focus:border-pink-500 focus:outline-none shadow-lg text-lg"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <div className="mt-2 text-sm text-gray-600">
                    Presiona <span className="font-semibold">Enter</span> para buscar o <button onClick={() => setSearchTerm('')} className="text-pink-500 underline">limpiar</button>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <a href="#anchetas" className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-xl transition transform hover:scale-105">Ver Colección 🎁</a>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-pink-200 to-purple-200 rounded-3xl p-6 hero-image-container hero-pulse">
                <div className="hero-image-shine">
                  <img src="https://cdn.abacus.ai/images/c439276b-c88b-4ab1-8ddb-d28b9543b7b4.png" alt="GiftBox Premium" className="rounded-2xl shadow-2xl" />
                </div>
                
                {/* Partículas decorativas flotantes */}
                <div className="hero-particle hero-particle-1">🎁</div>
                <div className="hero-particle hero-particle-2">💝</div>
                <div className="hero-particle hero-particle-3">🎀</div>
                <div className="hero-particle hero-particle-4">✨</div>
                <div className="hero-particle hero-particle-5">💫</div>
              </div>
            </div>
            </div>
          </div>
      </section>    

      {/* Separador Elegante */}
      <div className="section-divider">
        <div className="section-divider-lines"></div>
        <div className="section-divider-dots">
          <div className="section-divider-dot"></div>
          <div className="section-divider-dot"></div>
          <div className="section-divider-dot"></div>
        </div>
      </div>

      {/* Productos */}
      <section id="anchetas" className="py-16">
        <div className="max-w-6xl mx-auto px-4" id="products-section">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Colección <span className="text-pink-500">GiftBox</span></h2>
          
          {/* Filtros por Categoría */}
          <div className="flex justify-center mb-8">
            <div className="flex flex-wrap gap-2">
              {categoryOptions.map((category) => {
                const categoryData = categories.find(c => c.name === category);
                return (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setCurrentPage(1);
                      const productsSection = document.getElementById('anchetas');
                      if (productsSection) {
                        productsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${
                      selectedCategory === category
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-pink-100 border'
                    }`}
                  >
                    {categoryData && <span>{categoryData.icon}</span>}
                    {category}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 my-8">
              {/* <button 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg border ${
                  currentPage === 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                ← Anterior
              </button> */}
              
              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  const isFirstOrLast = pageNumber === 1 || pageNumber === totalPages;
                  const isCurrentPage = pageNumber === currentPage;
                  const showPage = isFirstOrLast || Math.abs(pageNumber - currentPage) <= 2;
                  
                  if (!showPage && pageNumber !== 1 && pageNumber !== totalPages) {
                    return null;
                  }
                  
                  if (!showPage && pageNumber === 2 && currentPage > 4) {
                    return (
                      <span key={pageNumber} className="px-3 py-2 text-gray-500">...</span>
                    );
                  }
                  
                  if (!showPage && pageNumber === totalPages - 1 && currentPage < totalPages - 3) {
                    return (
                      <span key={pageNumber} className="px-3 py-2 text-gray-500">...</span>
                    );
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`px-4 py-2 rounded-lg border ${
                        isCurrentPage 
                          ? 'bg-pink-500 text-white border-pink-500' 
                          : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
{/*               <button 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg border ${
                  currentPage === totalPages 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                Siguiente →
              </button> */}
            </div>
          )}

          {/* Products count */}
          <div className="text-center mb-6 text-gray-600">
            {searchTerm ? (
              <span>Resultados de búsqueda para "<strong>{searchTerm}</strong>": {filteredProducts.length} productos</span>
            ) : (
              <span>Mostrando {currentProducts.length} de {filteredProducts.length} productos</span>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {currentProducts.map((product) => (
              <div key={product.id} onClick={() => handleImageClick(product)} className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition transform hover:-translate-y-2 cursor-pointer ${product.discountedPrice ? 'ring-2 ring-red-200' : ''} ${!product.inStock ? 'opacity-60' : ''}`}>
                <div className="relative">
                  <img src={product.images[0]} alt={product.name} className="w-full h-48 object-cover" />
                  {/* Badges superiores */}
                  {/* Badge de tags */}
                  <div className="absolute top-2 left-2">
                    <span className="bg-gradient-to-r from-pink-500 to-purple-600 text-white text-xs px-3 py-1 rounded-full">
                      {product.tags[0]}
                    </span>
                  </div>
                  <span 
                    className="absolute top-3 right-3 text-white text-xs px-3 py-1 rounded-full"
                    style={{ 
                      backgroundColor: categories.find(c => c.name === product.category)?.color || '#6b7280' 
                    }}
                  >
                    {categories.find(c => c.name === product.category)?.icon || ''} {product.category}
                  </span>
                  <div className="absolute bottom-3 right-3 space-y-1">
                    {product.images.length > 1 && (
                      <div className="bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                        📷 {product.images.length} imágenes
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  {/* Badge de stock arriba del nombre */}
                  <div className={`${product.inStock ? 'bg-green-500' : 'bg-red-500'} text-white text-xs px-2 py-1 rounded-full font-medium inline-block mb-2`}>
                    {product.inStock ? `📦 ${product.stock} uds.` : '❌ Agotado'}
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                      {product.discountedPrice ? (
                        <>
                          <span className="text-sm text-gray-400 line-through">{formatPrice(product.price)}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xl font-bold text-green-600">{formatPrice(product.discountedPrice)}</span>
                            <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full font-bold">
                              -{product.discountPercentage}%
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="text-xl font-bold text-pink-500">{formatPrice(product.price)}</span>
                      )}
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!product.inStock) {
                          return;
                        }
                        addToCart(product, e);
                      }} 
                      className={`${product.inStock ? 'bg-pink-100 text-pink-600 hover:bg-pink-500 hover:text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'} p-2 rounded-full transition`}
                    >
                      🛒
                    </button>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 my-8">
 {/*              <button 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border ${currentPage === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}"
              >
                ← Anterior
              </button> */}
              
              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  const isFirstOrLast = pageNumber === 1 || pageNumber === totalPages;
                  const isCurrentPage = pageNumber === currentPage;
                  const showPage = isFirstOrLast || Math.abs(pageNumber - currentPage) <= 2;
                  
                  if (!showPage && pageNumber !== 1 && pageNumber !== totalPages) {
                    return null;
                  }
                  
                  if (!showPage && pageNumber === 2 && currentPage > 4) {
                    return (
                      <span key={pageNumber} className="px-3 py-2 text-gray-500">...</span>
                    );
                  }
                  
                  if (!showPage && pageNumber === totalPages - 1 && currentPage < totalPages - 3) {
                    return (
                      <span key={pageNumber} className="px-3 py-2 text-gray-500">...</span>
                    );
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`px-4 py-2 rounded-lg border ${
                        isCurrentPage 
                          ? 'bg-pink-500 text-white border-pink-500' 
                          : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
{/*               <button 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg border ${
                  currentPage === totalPages 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                }`}
              >
                Siguiente →
              </button> */}
            </div>
          )}
        </section>

        {/* Personalizado */}
      <section id="personalizado" className="py-16 bg-gradient-to-r from-pink-500 to-purple-600">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Crea Tu GiftBox Personalizado</h2>
              <p className="text-pink-100 mb-8 text-lg">Diseña tu propio GiftBox eligiendo cada producto, añade mensajes personalizados y hazlo único.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a href={`https://wa.me/${whatsappNumber}?text=Hola! Quiero crear un GiftBox personalizado`} target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-pink-600 px-8 py-3 rounded-full font-semibold hover:shadow-xl transition">Personalizar por WhatsApp →</a>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">🎁</p>
                  <p className="text-xs text-pink-100">Elige productos</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">💌</p>
                  <p className="text-xs text-pink-100">Mensaje personalizado</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-2xl font-bold">🎀</p>
                  <p className="text-xs text-pink-100">Empaque único</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6">
                <img 
                  src="https://cdn.abacus.ai/images/5e7b85be-045f-43b8-9f7e-045bb7b69845.png" 
                  alt="GiftBox Personalizado" 
                  className="w-full rounded-2xl shadow-2xl"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl">✨</div>
              <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-xl">🎁</div>
            </div>
          </div>
        </div>
      </section>

      {/* Empresas */}
      <section id="empresas" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Descuentos para Empresas</h2>
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto mb-8">
            <div className="bg-white p-4 rounded-xl shadow">
              <p className="text-2xl font-bold text-purple-600">15%</p>
              <p className="text-xs text-gray-500">+10 unidades</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow">
              <p className="text-2xl font-bold text-purple-600">25%</p>
              <p className="text-xs text-gray-500">+50 unidades</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow">
              <p className="text-2xl font-bold text-purple-600">35%</p>
              <p className="text-xs text-gray-500">+100 unidades</p>
            </div>
          </div>
          <a href={`https://wa.me/${whatsappNumber}?text=Hola! Quiero cotizar GiftBox para mi empresa`} target="_blank" rel="noopener noreferrer" className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-semibold">Solicitar Cotización</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="https://cdn.abacus.ai/images/ce62aaea-7192-4eab-8134-3539efec6757.png" alt="Logo" className="w-12 h-12 rounded-lg" />
            <span className="text-xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">GiftBox</span>
          </div>
          <p className="text-gray-400 text-sm mb-4">📍 Bogotá, Colombia | 📞 +57 324 685 5854 | ✉️ <a href='mailto:ventas@giftbox.com.co'>ventas@giftbox.com.co</a></p>
          <p className="text-gray-500 text-xs">© 2026 GiftBox. Todos los derechos reservados. Hecho con 💕</p>
        </div>
      </footer>

      {/* Botón flotante WhatsApp */}
      <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 bg-green-500 text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl shadow-lg hover:bg-green-600 transition hover:scale-110 z-40">
        💬
      </a>
    </div>
  );
}

export default App;